import { computed, signal } from "@preact/signals";
import type { Session as SupabaseSession } from "@supabase/supabase-js";
import type { JudgeEvent, JudgeRequest, MetaEvent } from "@fab-judge/schema";
import { ApiError } from "./api/client";
import { getConversation } from "./api/conversations";
import { sendFeedback, streamJudge } from "./api/judge";
import { getMe, type Me } from "./api/me";
import { supabase } from "./lib/supabase";
import { createLocalId } from "./lib/id";
import { invalidateHistoryCache } from "./lib/historyCache";
import {
  type AuthError,
  readAuthError,
  stripAuthError,
} from "./lib/oauthError";

/* ==========================================================================
   Auth session (Supabase)
   ========================================================================== */

export interface Session {
  email: string;
}

export const session = signal<Session | null>(null);

/** Profile + question allowance for the signed-in user, from `GET /me`. */
export const me = signal<Me | null>(null);

/**
 * Refresh the profile/allowance line. Best-effort: on failure the previous
 * value stands (or the UI falls back to the session email and hides the line).
 */
export async function refreshMe(): Promise<void> {
  if (!session.value) return;
  try {
    me.value = await getMe();
  } catch (error) {
    console.warn("me fetch failed", error);
  }
}

/** False until the persisted session resolves, to avoid a login flash. */
export const authReady = signal(false);

/**
 * A sign-in failure Supabase Auth sent back on the callback URL (see
 * `lib/oauthError.ts`). `null` when the last sign-in was clean.
 */
export const authError = signal<AuthError | null>(null);

/**
 * Lift the callback's error params into `authError` and clear them from the URL.
 *
 * Must run before rendering: the callback lands on a path the router rewrites
 * (`/` → `/login`, and `/login` → `/beta-full`), and a router redirect
 * replaces the whole URL, so reading the message later finds nothing. Clearing
 * it keeps a reload from resurrecting a stale banner.
 */
export function captureAuthError(): void {
  const current = window.location.href;
  const error = readAuthError(current);
  if (!error) return;
  authError.value = error;
  window.history.replaceState(null, "", stripAuthError(current));
}

/** Drop a captured sign-in error (the user is trying again). */
export function dismissAuthError(): void {
  authError.value = null;
}

function toSession(next: SupabaseSession | null): Session | null {
  return next?.user ? { email: next.user.email ?? "" } : null;
}

/** Resolve the persisted session and keep the signal in sync with auth events. */
export async function initAuth(): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    session.value = toSession(data.session);
  } catch (error) {
    console.warn("auth init failed", error);
  } finally {
    authReady.value = true;
  }

  supabase.auth.onAuthStateChange((event, next) => {
    session.value = toSession(next);
    // INITIAL_SESSION is covered by the explicit refresh below.
    if (event === "INITIAL_SESSION") return;
    if (next?.user) void refreshMe();
    else me.value = null;
  });

  if (session.value) void refreshMe();
}

export async function signOut(): Promise<void> {
  stopStreaming();
  session.value = null;
  me.value = null;
  turns.value = [];
  conversationId.value = null;
  publicConversations.value = new Set();
  invalidateHistoryCache();
  try {
    await supabase.auth.signOut();
  } catch {
    /* ignore */
  }
}

/* ==========================================================================
   Conversation state
   ========================================================================== */

export type TurnStatus = "thinking" | "streaming" | "match" | "done" | "error";

export interface Turn {
  id: string;
  question: string;
  answer: string;
  status: TurnStatus;
  error?: string;
  meta?: MetaEvent;
  feedback?: "up" | "down";
  /**
   * A semantic cache match presented for this turn: the similar question the
   * cache holds an answer for, and the entry id to view it. Set while the
   * turn waits for the user to choose "view answer" or "ask anyway".
   */
  match?: { id: string; eventId: string; question: string };
}

export const turns = signal<Turn[]>([]);

/**
 * Server-owned conversation id, captured from the `start` event. `null` means
 * the next question starts a new conversation; set means follow-ups continue it.
 */
export const conversationId = signal<string | null>(null);

/**
 * Conversations known to be public during this session. Populated when a
 * public conversation is opened from history and after a successful share, so
 * the share flow does not repeat the "this makes it public" warning.
 */
export const publicConversations = signal<ReadonlySet<string>>(new Set());

export function markConversationPublic(id: string): void {
  if (publicConversations.value.has(id)) return;
  const next = new Set(publicConversations.value);
  next.add(id);
  publicConversations.value = next;
}

export const isStreaming = computed(() =>
  turns.value.some((t) => t.status === "thinking" || t.status === "streaming")
);

/** True while a turn is waiting on the user's match-presentation choice. */
export const hasPendingMatch = computed(() =>
  turns.value.some((t) => t.status === "match")
);

/**
 * True while the daily-limit dialog is open. Set when a model-backed question
 * is rejected with `daily_limit_reached`; the dialog reads `me.nextAvailableAt`
 * for its countdown.
 */
export const dailyLimitReached = signal(false);

export function dismissDailyLimit(): void {
  dailyLimitReached.value = false;
}

/**
 * True when `GET /me` says no questions are left — the next model-backed
 * question would be rejected with `daily_limit_reached`. A `null` count (not
 * loaded yet, or the fetch failed) counts as unknown and lets the request
 * through: the server stays authoritative.
 */
function hasNoQuestionsLeft(): boolean {
  const questionsLeft = me.value?.questionsLeft;
  return questionsLeft != null && questionsLeft <= 0;
}

let activeController: AbortController | null = null;

function updateTurn(id: string, patch: (turn: Turn) => Turn) {
  turns.value = turns.value.map((t) => (t.id === id ? patch(t) : t));
}

function removeTurn(id: string) {
  turns.value = turns.value.filter((t) => t.id !== id);
}

function mergeByName<T extends { name: string }>(
  prev: T[] = [],
  next: T[] = [],
): T[] {
  const map = new Map(prev.map((item) => [item.name, item]));
  for (const item of next) map.set(item.name, item);
  return [...map.values()];
}

function mergeByTitle<T extends { title: string }>(
  prev: T[] = [],
  next: T[] = [],
): T[] {
  const map = new Map(prev.map((item) => [item.title, item]));
  for (const item of next) map.set(item.title, item);
  return [...map.values()];
}

function applyEvent(turn: Turn, event: JudgeEvent): Turn {
  switch (event.type) {
    case "start":
      return turn;
    case "meta": {
      // `meta` is additive: entities may arrive before tokens and citations
      // after them, so merge rather than replace.
      const prev = turn.meta;
      return {
        ...turn,
        meta: {
          type: "meta",
          format: event.format ?? prev?.format ?? null,
          cards: mergeByName(prev?.cards, event.cards),
          sources: mergeByTitle(prev?.sources, event.sources),
          cached: event.cached ?? prev?.cached,
          matchedQuestion: event.matchedQuestion ?? prev?.matchedQuestion,
        },
      };
    }
    case "match":
      return {
        ...turn,
        status: "match",
        match: {
          id: event.id,
          eventId: event.eventId,
          question: event.question,
        },
      };
    case "token":
      return { ...turn, status: "streaming", answer: turn.answer + event.text };
    case "done":
      // A match-presentation stream ends with `done` too; keep the turn in
      // "match" so the choice stays visible until the user acts on it.
      return { ...turn, status: turn.status === "match" ? "match" : "done" };
    case "error":
      return { ...turn, status: "error", error: event.message };
  }
}

/** The message list the backend sees for the current thread. */
function messagesFromTurns(turns: Turn[]): JudgeRequest["messages"] {
  return turns.flatMap((t) => [
    { role: "user" as const, content: t.question },
    ...(t.answer ? [{ role: "assistant" as const, content: t.answer }] : []),
  ]);
}

/** Stream a set of messages into an existing turn. */
async function streamInto(
  turnId: string,
  messages: JudgeRequest["messages"],
  options: {
    noCache?: boolean;
    cacheId?: string;
    eventId?: string;
    retry?: boolean;
    cacheDecision?: JudgeRequest["cacheDecision"];
  } = {},
): Promise<void> {
  activeController?.abort();
  const controller = new AbortController();
  activeController = controller;

  try {
    for await (
      const event of streamJudge(
        {
          conversationId: conversationId.value,
          messages,
          noCache: options.noCache,
          cacheId: options.cacheId,
          eventId: options.eventId,
          retry: options.retry,
          cacheDecision: options.cacheDecision,
        },
        controller.signal,
      )
    ) {
      // The start event carries the conversation id for this thread.
      if (event.type === "start") {
        const isNew = conversationId.value !== event.id;
        conversationId.value = event.id;
        // A new thread changes the history list; force the next open to reload.
        if (isNew) invalidateHistoryCache();
      }
      updateTurn(turnId, (t) => applyEvent(t, event));
    }
  } catch (error) {
    if ((error as DOMException)?.name === "AbortError") {
      updateTurn(turnId, (t) => ({ ...t, status: "done" }));
    } else if (error instanceof ApiError && error.status === 401) {
      updateTurn(turnId, (t) => ({
        ...t,
        status: "error",
        error: "Your session expired. Please sign in again.",
      }));
      void signOut();
    } else if (
      error instanceof ApiError &&
      error.code === "daily_limit_reached"
    ) {
      // Nothing was generated and no conversation was created, so surface the
      // limit dialog instead of an error bubble. A match-presentation turn is
      // restored to its choice card (the free "View answer" is still on
      // offer); otherwise the empty turn is dropped.
      const turn = turns.value.find((t) => t.id === turnId);
      if (turn?.match) {
        updateTurn(turnId, (t) => ({ ...t, status: "match" }));
      } else {
        removeTurn(turnId);
      }
      dailyLimitReached.value = true;
    } else {
      updateTurn(turnId, (t) => ({
        ...t,
        status: "error",
        error: error instanceof Error ? error.message : "Something went wrong.",
      }));
    }
  } finally {
    if (activeController === controller) activeController = null;
    // A model-backed query (or a released slot) moves the allowance; keep the
    // line fresh.
    void refreshMe();
  }
}

/** Append a question, stream the judge's answer into a new turn. */
export async function submitQuestion(question: string) {
  const trimmed = question.trim();
  if (!trimmed || isStreaming.value) return;

  // Full conversation the backend sees: prior turns plus the new question.
  const messages = [
    ...messagesFromTurns(turns.value),
    { role: "user" as const, content: trimmed },
  ];

  const id = createLocalId("turn");
  turns.value = [
    ...turns.value,
    { id, question: trimmed, answer: "", status: "thinking" },
  ];

  await streamInto(id, messages);
}

/**
 * The user chose "view answer" on a match-presentation turn: serve the cached
 * answer for the similar question (free, no model run).
 */
export async function viewCachedAnswer(id: string) {
  const turn = turns.value.find((t) => t.id === id);
  if (!turn || isStreaming.value || !turn.match) return;
  const match = turn.match;
  // Clear the choice so a delayed second click cannot fire it again.
  updateTurn(id, (t) => ({ ...t, status: "thinking", match: undefined }));
  await streamInto(id, messagesFromTurns(turns.value), {
    cacheId: match.id,
    eventId: match.eventId,
    cacheDecision: "view",
  });
}

/**
 * The user chose "ask anyway" on a match-presentation turn: run the model on
 * their actual question (a model run). `cacheId` is echoed back only so the
 * server can log the choice.
 */
export async function askAnyway(id: string) {
  const turn = turns.value.find((t) => t.id === id);
  if (!turn || isStreaming.value || !turn.match) return;
  const match = turn.match;
  // "thinking" both gives immediate feedback and blocks a second click while
  // the allowance line is refreshed. `match` is kept on the turn so a rejection
  // can restore the choice card.
  updateTurn(id, (t) => ({ ...t, status: "thinking" }));

  // This is the one choice on a match turn that always runs the model, so with
  // no questions left show the limit dialog instead of a round-trip that 429s.
  // Refresh first: the window may have rolled since the last question.
  await refreshMe();
  if (hasNoQuestionsLeft()) {
    updateTurn(id, (t) => ({ ...t, status: "match", match }));
    dailyLimitReached.value = true;
    return;
  }

  await streamInto(id, messagesFromTurns(turns.value), {
    noCache: true,
    cacheId: match.id,
    eventId: match.eventId,
    cacheDecision: "ask_anyway",
  });
}

/**
 * Detach the client from the in-flight stream (navigation, sign-out). The server
 * keeps generating and persists the answer, so it appears in history later — the
 * user has no cancel option, and a disconnect must not hand the question's slot back.
 */
export function stopStreaming() {
  activeController?.abort();
  activeController = null;
}

export function resetConversation() {
  stopStreaming();
  conversationId.value = null;
  turns.value = [];
}

/**
 * Replace the current thread with a saved conversation from history.
 * Messages arrive as a flat user/assistant stream; fold them back into turns.
 */
export async function openConversation(id: string): Promise<void> {
  stopStreaming();
  const detail = await getConversation(id);

  const next: Turn[] = [];
  for (const message of detail.messages) {
    const meta = message.meta
      ? { type: "meta" as const, ...message.meta }
      : undefined;
    if (message.role === "user") {
      next.push({
        id: message.id,
        question: message.content,
        answer: "",
        status: "done",
      });
      continue;
    }

    const last = next[next.length - 1];
    if (last && !last.answer) {
      last.answer = message.content;
      last.meta = meta;
    } else {
      next.push({
        id: message.id,
        question: "",
        answer: message.content,
        status: "done",
        meta,
      });
    }
  }

  // A turn that never got an answer (the run failed, and the server persists no
  // assistant row) comes back from history as a question with an empty answer.
  // Show it as the failure it was, so the notice — and, on the newest turn, the
  // Retry button — is there after a reload too.
  for (const turn of next) {
    if (!turn.answer) {
      turn.status = "error";
      turn.error = "This ruling didn't finish.";
    }
  }

  conversationId.value = detail.id;
  turns.value = next;
}

/**
 * Retry asks the model again in place: the conversation and every prior turn
 * are kept, and only the newest turn's answer is redone. Available on any
 * completed newest turn (cached or not) and on a failed one; the billing and
 * cache telemetry stay server-side (`cacheDecision: "retry"` only when the
 * replaced answer was cache-served).
 */
export async function retryTurn(id: string) {
  const turn = turns.value.find((t) => t.id === id);
  if (!turn || isStreaming.value) return;
  // Retry regenerates the newest turn only (the server replaces exactly that
  // turn), so ignore a stale click on an older one.
  if (turns.value.at(-1)?.id !== id) return;

  const question = turn.question;
  const wasCached = Boolean(turn.meta?.cached);

  // Retry always runs the model, so bail out before dropping the stored answer
  // when no questions are left. "thinking" (with the old answer/meta
  // cleared) both gives feedback and blocks a second click while the allowance
  // line is refreshed.
  updateTurn(id, (t) => ({
    ...t,
    status: "thinking",
    answer: "",
    error: undefined,
    meta: undefined,
  }));
  await refreshMe();
  if (hasNoQuestionsLeft()) {
    updateTurn(id, () => turn);
    dailyLimitReached.value = true;
    return;
  }

  // In place: keep the conversation and every prior turn; only this turn's
  // answer is redone. The stale answer is omitted from the context so the
  // retried question is re-asked from the same prior history.
  const messages = [
    ...messagesFromTurns(turns.value.slice(0, -1)),
    { role: "user" as const, content: question },
  ];
  void streamInto(id, messages, {
    noCache: true,
    retry: true,
    cacheDecision: wasCached ? "retry" : undefined,
  });
}

/** Record the user's rating for an answer, and send it to the backend. */
export function rateTurn(id: string, rating: "up" | "down") {
  const turn = turns.value.find((t) => t.id === id);
  if (!turn) return;

  const next = turn.feedback === rating ? undefined : rating;
  updateTurn(id, (t) => ({ ...t, feedback: next }));

  if (next && conversationId.value) {
    void sendFeedback(conversationId.value, next);
  }
}
